import { IAuthorWithEmail } from '@publishing-engine/domain/models';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  fetchAuthorProfileData,
  fetchOrganization,
  fetchUserOrganizationWorkspaces,
  getBaseJournalId
} from 'src/api/queries';
import { ISubmissionAuthor } from 'src/contexts/FormContext';
import { useWorkspaceData } from 'src/contexts/WorkspaceProvider';

export const useWorkspace = () => {
  const { shortname = '' } = useParams();
  const { workspace, isLoading: isLoadingWorkspace } = useWorkspaceData();

  const { id, organizationId } = workspace;

  const { data: submissionContextData } = useQuery({
    queryKey: ['base-journal-id'],
    queryFn: () => (organizationId ? getBaseJournalId(organizationId) : null)
  });
  return {
    workspaceShortname: shortname,
    workspaceId: id,
    orgId: organizationId,
    isLoadingWorkspace,
    submissionContextId: submissionContextData?.submissionContextId ?? ''
  };
};

export const getOrgId = () => {
  const { shortname = '' } = useParams();
  const { data: organization } = useQuery({
    queryKey: [`organization-${shortname}`],
    queryFn: () => fetchOrganization(shortname),
    retry: 0
  });

  const orgId = organization?.id ?? '';

  return orgId;
};

export const useAuthorData = (author: IAuthorWithEmail | ISubmissionAuthor) => {
  const orgId = getOrgId();

  return useQuery({
    queryKey: ['workspace-author-profile', { orgId, userId: author?.id }],
    queryFn: () =>
      author?.id ? fetchAuthorProfileData(orgId, author?.id) : null
  });
};

// This method checks whether a user is an editorial user.
// fetchUserOrganization(): This method returns a collection of organization data associated with a given userId.
// If the returned collection array contains data, the user is considered an editorial user.
export const useAuthorRoleCheck = (userId: string, orgId?: string) => {
  const { data, isError } = useQuery({
    queryKey: ['workspace-author-role-check', { orgId, userId }],
    queryFn: () =>
      userId && orgId ? fetchUserOrganizationWorkspaces(userId, orgId) : null
  });

  return !isError && data
    ? !data.collection.length && data?.userStatus === 'active'
    : false;
};
